import * as React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { SERVER_IP, httpOption } from '../Util/Config';
import { asyncTokenCheck } from '../Util/AuthUtility';

const propTypes = {
  children: PropTypes.node,
};
const defaultProps = {
  children: <></>,
};

const { Provider, Consumer } = React.createContext();
/**
 * time stream Context hoc
 */
export const withHoc = Component => {
  const comp = (props) => {
    return <Consumer>{data => <Component {...props} {...data} />}</Consumer>;
  }
  return comp;
};
/**
 * Context 변수의 기본 값
 */
const defaultValue = {
  AUTH: {},
  CH: {
    list: [{ list: [{}] }],
    load: false,
  },
  EPG: {
    type: '',
    epgList: [],
    load: false,
  },
  LOAD_VOD: false,
  VOD_MAIN: [],
  VOD_CAST: [],
  VOD_CATE: [],
  VOD_DETAIL: [],
  SIGN_STATUS: 0,
  COOKIE_NAME: 'TIME_AUTH',
  TOKEN_CHECK_INIT: false,
};

export class DataProvider extends React.Component {

  /**
   * Context에 state변수 선언 
   * auth: 유저정보
   * signStatus: 로그인 status code
   * channels: 채널정보
   * epgs: epg 정보
   * isTokenCheck: 토큰 값이 유효한지 체크
   */
  state = {
    auth: defaultValue.AUTH,
    signStatus: defaultValue.SIGN_STATUS,
    channels: defaultValue.CH,
    epgs: defaultValue.EPG,
    loadVOD: defaultValue.LOAD_VOD,
    vodMain: defaultValue.VOD_MAIN,
    vodCast: defaultValue.VOD_CAST,
    vodCate: defaultValue.VOD_CATE,
    vodDetail: defaultValue.VOD_DETAIL,
    isTokenCheck: defaultValue.TOKEN_CHECK_INIT,
  };

  /**
   * state 데이터를 초기화한다
   */
  dataInit = () => {
    this.setState({
      auth: defaultValue.AUTH,
      signStatus: defaultValue.SIGN_STATUS,
      channels: defaultValue.CH,
      epgs: defaultValue.EPG,
      loadVOD: defaultValue.LOAD_VOD,
      vodMain: defaultValue.VOD_MAIN,
      vodCast: defaultValue.VOD_CAST,
      vodCate: defaultValue.VOD_CATE,
      vodDetail: defaultValue.VOD_DETAIL,
      isTokenCheck: defaultValue.TOKEN_CHECK_INIT,
    });
  };
  /**
   * 로그인
   */
  signIn = (id, pwd, callback) => {
    window.$.ajax({
      url: `${SERVER_IP}/cli/user/login`,
      type: 'post',
      ...httpOption,
      data: {
        id,
        pwd,
      },
    })
      .done(res => {
        if (res.status === 200) {
          if (res.Auth.is_admin) {
            window.location.href = '/cms';
          } else {
            this.setState(
              update(this.state, {
                auth: { $set: res.Auth },
                signStatus: { $set: res.status },
                isTokenCheck: { $set: true },
              }),
            );
          }
        }
      })
      .fail(res => {
        if (res.status === 403) {
          this.setState(update(this.state, {
            auth: { $set: res.responseJSON.Auth },
            signStatus: { $set: res.responseJSON.status },
            isTokenCheck: { $set: true },
          }));
        } else {
          this.setState({ signStatus: res.status });
        }
      });
    if (callback) {
      callback();
    }
  };

  /**
   * 유저정보를 업데이트하는 함수
   */
  authInfoUpdate = (auth, status, callback) => {
    this.setState(
      {
        auth: auth,
        signStatus: status,
        isTokenCheck: true,
      },
      () => {
        if (callback) {
          callback();
        }
      },
    );
  };

  /**
   * 로그아웃을 하고 쿠키를 삭제한다.
   */
  signOut = callback => {
    window.$.ajax({
      url: `${SERVER_IP}/cli/user/delCookie`,
      type: 'get',
      ...httpOption,
      xhrFields: {
        withCredentials: true,
      },
    }).then(data => {
      if (data.status === 200) {
        window.location.href = '/tv';
      }
    });
    if (callback) {
      callback();
    }
  };

  /**
   * 토큰값이 유요한지 체크한다.
   */
  tokenCheck = async callback => {
    try {
      await asyncTokenCheck();
    } catch (e) {
      this.setState({ isTokenCheck: false });
    }
    window.$.ajax({
      url: `${SERVER_IP}/cli/user/check`,
      type: 'get',
      ...httpOption,
      xhrFields: {
        withCredentials: true,
      },
    }).then(res => {
      /**
       * 200: 성공
       * 401: 토큰만료
       * 404: 토큰없음
       */
      if (res.status === 200) {
        if (this.state.auth._id === undefined) {
          this.setState(
            {
              auth: res.Auth,
              signStatus: res.status,
              isTokenCheck: true,
            },
            () => {
              if (callback) {
                callback();
              }
            },
          );
        }
      } else if (res.status === 401) {
        this.setState({ isTokenCheck: false });
      } else if (res.status === 404) {
        this.setState({ isTokenCheck: false });
      } else if (res.status === 403) {
        this.setState({ isTokenCheck: true });
        window.location.href = '/expired';
      }
    });
  };

  /**
   * 채널정보를 업데이트 한다.
   */
  chUpdate = async (signStatus, callback) => {
    if (signStatus === 200) {
      const { auth } = this.state;
      const chPlan = auth.use_plan.match(/^[A-Za-z-]*/g)[0];
      await window.$.ajax({
        url: `${SERVER_IP}/cli/channel/${chPlan}`,
        type: 'get',
        ...httpOption,
      }).then(res => {
        if (res.status === 200) {
          this.setState(
            { channels: { list: res.channels, load: true } },
            () => {
              if (callback) {
                callback();
              }
            },
          );
        } else {
          window.location.href = '/tv';
        }
      });
    } else {
      await this.setState({ channels: { list: defaultValue.CH } });
    }
  };

  /**
   * epg정보를 업데이트한다.
   */
  epgUpdate = async (epgid, callback) => {
    this.setState(
      update(this.state, {
        epgs: { load: { $set: false } },
      }),
    );
    await window.$.ajax({
      url: `${SERVER_IP}/cli/epg/${epgid}`,
      type: 'get',
      ...httpOption,
    })
      .then(res => {
        this.setState({ epgs: { ...res, load: true } }, () => {
          if (callback) {
            callback();
          }
        });
      })
      .catch(() => {
        window.$.ajax(this);
      });
  };

  /**
   * vod메인list를 업데이트한다.
   */
  vodUpdate = async (callback) => {
    this.setState(
      update(this.state, {
        loadVOD: { $set: false },
      }),
    )

    await window.$.ajax({
      url: `${SERVER_IP}/cli/vod`,
      type: 'get',
      ...httpOption,
    })
      .then((res) => {
        this.setState(update(this.state, {
          vodMain: { $set: res.vod },
          loadVOD: { $set: true },
        }), () => {
          if (callback) {
            callback()
          }
        })
      })
      .catch(() => {
        window.$.ajax(this)
      })
  }

  vodCategoryUpdate = async (castID?, cateID?, page?, callback) => {
    this.setState(
      update(this.state, {
        loadVOD: { $set: false },
      })
    )

    await window.$.ajax({
      url: `${SERVER_IP}/cli/vod/${castID}?cate=${cateID}&page=${page}`,
      type: 'get',
      ...httpOption,
    })
      .then((res) => {
        this.setState(update(this.state, {
          vodCate: { $set: res.list },
          loadVOD: { $set: true },
        }), () => {
          if (callback) {
            callback()
          }
        })
      })
      .catch(() => {
        window.$.ajax(this)
      })
  }

  vodCastUpdate = async (castId, callback) => {
    this.setState(
      update(this.state, {
        loadVOD: { $set: false },
      }),
    )

    await window.$.ajax({
      url: `${SERVER_IP}/cli/vod/${castId}`,
      type: 'get',
      ...httpOption,
    })
      .then((res) => {
        this.setState(update(this.state, {
          vodCast: { $set: res.vod },
          loadVOD: { $set: true },
        }), () => {
          if (callback) {
            callback()
          }
        })
      })
      .catch(() => {
        window.$.ajax(this)
      })
  }

  vodDetailUpdate = async (idKey: Number, callback) => {
    this.setState(
      update(this.state, {
        loadVOD: { $set: false },
      }),
    )

    await window.$.ajax({
      url: `${SERVER_IP}/cli/vod?program=${idKey}`,
      type: 'get',
      ...httpOption,
    })
      .then((res) => {
        this.setState(
          update(this.state, {
            vodDetail: { $set: res.vod },
            loadVOD: { $set: true },
          }), () => {
            if (callback) {
              callback()
            }
          }
        )
      })
      .catch(() => {
        window.$.ajax(this)
      })
  }

  render() {
    const {
      auth,
      signStatus,
      channels,
      epgs,
      isTokenCheck,
      loadVOD,
      vodMain,
      vodCast,
      vodCate,
      vodDetail,
    } = this.state;
    return (
      <Provider
        value={{
          auth,
          signStatus,
          channels,
          epgs,
          vodMain,
          vodCast,
          vodCate,
          vodDetail,
          isTokenCheck,
          loadVOD,
          dataInit: this.dataInit,
          authInfoUpdate: this.authInfoUpdate,
          signIn: this.signIn,
          signOut: this.signOut,
          tokenCheck: this.tokenCheck,
          chUpdate: this.chUpdate,
          epgUpdate: this.epgUpdate,
          vodUpdate: this.vodUpdate,
          vodCastUpdate: this.vodCastUpdate,
          vodCategoryUpdate: this.vodCategoryUpdate,
          vodDetailUpdate: this.vodDetailUpdate,
        }}
      >
        {this.props.children}
      </Provider>
    );
  }
}

DataProvider.propTypes = propTypes;
DataProvider.defaultProps = defaultProps;
